import { Component, Inject, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';

import { ViewDidEnter, ViewWillLeave } from '@ionic/angular';

import { Subject, Subscription } from 'rxjs';

import { VendorSorterType } from '../../core/enums/vendor-sorter-type.enum';

import { Tag } from '../../core/models/tag.model';

import { AnalyticsService } from '../../core/services/analytics.service';
import { ModalService } from '../../core/services/modal.service';
import { PlatformService } from '../../core/services/ssr/platform.service';
import { VendorsService } from '../../core/services/vendors.service';

import { AnalyticsConfig, TOKEN_ANALYTICS_CONFIG } from '../../core/config/analytics.config';
import { BACK_BUTTON_HANDLER_PRIORITY } from '../../core/config/app.config';

import { Helpers } from '../../core/helpers';

export const TagsModalPageIdentifier = 'tags-modal';

export interface TagsModalResult {
  tags: Array<Tag>;
  sorterType: VendorSorterType;
}

@Component({
  selector: 'app-tags-modal',
  templateUrl: './tags-modal.page.html',
  styleUrls: ['./tags-modal.page.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TagsModalPage implements OnInit, ViewDidEnter, ViewWillLeave, OnDestroy {
  @Input()
  public tags: Array<Tag>;

  @Input()
  public selectedSorterType: VendorSorterType;

  public sorterTypes: Array<{ value: VendorSorterType; text: string }>;
  public VendorSorterType = VendorSorterType;

  private selectedTagWhenOpening: Array<number>;
  private selectedSorterTypeWhenOpening: VendorSorterType;
  private unregisterBackButtonSubscription: Subscription;
  private unsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private modalService: ModalService,
    private analyticsService: AnalyticsService,
    private vendorsService: VendorsService,
    private platformService: PlatformService,
    @Inject(TOKEN_ANALYTICS_CONFIG) private analyticsConfig: AnalyticsConfig,
  ) {}

  ngOnInit() {
    this.selectedTagWhenOpening = this.getSelectedTagIds(this.tags);
    this.selectedSorterTypeWhenOpening = this.selectedSorterType;

    this.sorterTypes = [
      { value: VendorSorterType.Rating, text: 'TAGS_MODAL_PAGE.BY_RATING' },
      { value: VendorSorterType.DeliveryTime, text: 'TAGS_MODAL_PAGE.BY_DELIVERY_TIME' },
    ];

    const recommendedSorterType = this.vendorsService.getRecommendedSorterType();

    if (recommendedSorterType !== VendorSorterType.Rating && recommendedSorterType !== VendorSorterType.DeliveryTime) {
      this.sorterTypes.unshift({ value: recommendedSorterType, text: 'TAGS_MODAL_PAGE.BY_RECOMMENDED' });
    }
  }

  ionViewDidEnter() {
    void this.analyticsService.trackView({ name: this.analyticsConfig.pageName.tagsFilterPage });
    this.unregisterBackButtonSubscription = this.platformService.backButton.subscribeWithPriority(BACK_BUTTON_HANDLER_PRIORITY, () => {});
  }

  ionViewWillLeave() {
    this.unregisterBackButtonSubscription?.unsubscribe();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public onRemoveAll(): void {
    this.tags.forEach((tag) => (tag.selected = false));

    this.trackEvent(this.analyticsConfig.eventName.tagsFilterPageClearAll, null);

    this.onApply();
  }

  public onApply(): void {
    const selectedTagIds = this.getSelectedTagIds(this.tags);

    const selectedTagsHaveChanged = !Helpers.areArraysEquivalent(this.selectedTagWhenOpening, selectedTagIds);
    const selectedSorterTypeHasChanged = this.selectedSorterTypeWhenOpening !== this.selectedSorterType;

    if (selectedTagsHaveChanged || selectedSorterTypeHasChanged) {
      this.trackEvent(this.analyticsConfig.eventName.tagsFilterPageApply, {
        [this.analyticsConfig.propertyName.selectedTagsId]: selectedTagIds.join(', '),
        [this.analyticsConfig.propertyName.selectedSorterId]: this.selectedSorterType,
      });

      void this.dismiss({
        ...(selectedTagsHaveChanged && { tags: this.tags }),
        ...(selectedSorterTypeHasChanged && { sorterType: this.selectedSorterType }),
      });
    } else {
      this.trackEvent(this.analyticsConfig.eventName.tagsFilterPageClose, {
        [this.analyticsConfig.propertyName.selectedTagsId]: selectedTagIds.join(', '),
        [this.analyticsConfig.propertyName.selectedSorterId]: this.selectedSorterType,
      });

      void this.dismiss();
    }
  }

  private getSelectedTagIds(tags: Array<Tag>): Array<number> {
    return tags.filter((tag) => tag.selected).map((tag) => tag.tagId);
  }

  private dismiss(filters?: { tags?: Array<Tag>; sorterType?: VendorSorterType }): Promise<boolean> {
    return this.modalService.dismissModal({ data: filters, id: TagsModalPageIdentifier });
  }

  private trackEvent(name: string, data: Record<string, unknown>): void {
    void this.analyticsService.trackEvent({ name, data });
  }
}
